import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { ContactUsSubmitApi } from '../../apis/api/ContactUsApi';
import { validateField, validateAll } from '../../utils/contactValidation';
import './ContactPage.css';

const INITIAL_FORM = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  country: '',
  inquiryType: null,
  message: '',
};

const ContactPage = () => {
  const dispatch = useDispatch();
  const { loading: apiLoading, error: apiError, successMessage } = useSelector((state) => state.ContactUsSlice);

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('revealed')),
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.scroll-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const inquiryTypeOptions = [
    { value: 'membership', label: 'Membership Information' },
    { value: 'partnership', label: 'Partnership Opportunity' },
    { value: 'general', label: 'General Inquiry' },
  ];

  // Validate a single field and update errors state
  const runFieldValidation = useCallback((name, value) => {
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Re-validate on change only if field was already touched
    if (touched[name]) runFieldValidation(name, value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    runFieldValidation(name, value);
  };

  const handleSelectChange = (selected) => {
    setFormData((prev) => ({ ...prev, inquiryType: selected }));
    setTouched((prev) => ({ ...prev, inquiryType: true }));
    const error = validateField('inquiryType', selected);
    setErrors((prev) => ({ ...prev, inquiryType: error }));
  };

  const handleSelectBlur = () => {
    setTouched((prev) => ({ ...prev, inquiryType: true }));
    const error = validateField('inquiryType', formData.inquiryType);
    setErrors((prev) => ({ ...prev, inquiryType: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allTouched = Object.keys(INITIAL_FORM).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setTouched(allTouched);

    const validationErrors = validateAll(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    // Build payload — send inquiryType as its string value
    const payload = {
      ...formData,
      inquiryType: formData.inquiryType?.value ?? '',
    };

    const result = await dispatch(ContactUsSubmitApi(payload));

    if (ContactUsSubmitApi.fulfilled.match(result)) {
      setSubmitSuccess(true);
      setTimeout(() => {
        setFormData(INITIAL_FORM);
        setErrors({});
        setTouched({});
        setSubmitSuccess(false);
      }, 4000);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const inputClass = (name) =>
    `form-input${errors[name] && touched[name] ? ' input-error' : touched[name] && !errors[name] ? ' input-valid' : ''}`;

  const selectErrorStyle = errors.inquiryType && touched.inquiryType;

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '51px',
      padding: '0 4px',
      border: selectErrorStyle
        ? '1px solid #ef4444'
        : state.isFocused
          ? '1px solid var(--theme-border-focus)'
          : '1px solid var(--theme-border)',
      borderRadius: '10px',
      boxShadow: selectErrorStyle
        ? '0 0 0 3px rgba(239,68,68,0.1)'
        : state.isFocused
          ? '0 0 0 3px var(--theme-primary-light)'
          : 'none',
      backgroundColor: 'var(--theme-bg-input)',
      '&:hover': { borderColor: selectErrorStyle ? '#ef4444' : 'var(--theme-border-focus)' },
      fontSize: '15px',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    }),
    valueContainer: (provided) => ({ ...provided, padding: '0 8px' }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'var(--theme-primary)'
        : state.isFocused
          ? 'var(--theme-primary-light)'
          : 'var(--theme-bg-dropdown)',
      color: state.isSelected ? '#ffffff' : 'var(--theme-text-primary)',
      padding: '12px 16px',
      cursor: 'pointer',
      fontSize: '15px',
      '&:active': { backgroundColor: 'var(--theme-primary)' },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '10px',
      boxShadow: 'var(--theme-shadow-hover)',
      overflow: 'hidden',
      marginTop: '4px',
      backgroundColor: 'var(--theme-bg-dropdown)',
      border: '1px solid var(--theme-border)',
    }),
    placeholder: (provided) => ({ ...provided, color: '#9ca3af', fontSize: '15px' }),
    singleValue: (provided) => ({ ...provided, color: 'var(--theme-text-primary)', fontSize: '15px' }),
    indicatorSeparator: (provided) => ({ ...provided, backgroundColor: 'var(--theme-border)' }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'var(--theme-text-secondary)',
      '&:hover': { color: 'var(--theme-primary)' },
    }),
  };

  return (
    <div className="contact-page">
      {/* Page Header */}
      <section className="contact-header-section animate-fade-in">
        <div className="container">
          <div className="contact-header-content">
            <h1 className="contact-page-title animate-fade-in-up">Contact Us</h1>
            <p className="contact-page-subtitle animate-fade-in-up animate-delay-200">
              Connect with a trusted global logistics network
            </p>
            <p className="contact-page-description animate-fade-in-up animate-delay-300">
              We welcome inquiries from freight forwarders, logistics partners, and industry professionals
              interested in collaboration or membership. Our team is committed to providing timely,
              professional, and confidential responses to support your business needs and growth objectives.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section" id="contact-form-section">
        <div className="container">
          <div className="contact-form-layout">
            <div className="form-column scroll-reveal">
              <h2 className="form-section-title">Send Us a Message</h2>
              <p className="form-section-description">
                Complete the form below and our team will respond promptly.
              </p>

              {submitSuccess && (
                <div className="form-success-message animate-scale-in" role="alert">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <p>{successMessage || 'Thank you for contacting us. Our team will be in touch shortly.'}</p>
                </div>
              )}

              {apiError && !submitSuccess && (
                <div className="form-error-message" role="alert">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <p>{apiError || 'Something went wrong. Please try again.'}</p>
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit} enctype="multipart/form-data" noValidate>
                {/* Row 1: Company + Contact Name */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companyName" className="form-label">Company Name *</label>
                    <input
                      id="companyName"
                      type="text"
                      name="companyName"
                      className={inputClass('companyName')}
                      value={formData.companyName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Your company name"
                      aria-invalid={!!errors.companyName}
                      aria-describedby="companyName-error"
                    />
                    {errors.companyName && touched.companyName && (
                      <span id="companyName-error" className="field-error" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {errors.companyName}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactName" className="form-label">Contact Person Name *</label>
                    <input
                      id="contactName"
                      type="text"
                      name="contactName"
                      className={inputClass('contactName')}
                      value={formData.contactName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Your full name"
                      aria-invalid={!!errors.contactName}
                      aria-describedby="contactName-error"
                    />
                    {errors.contactName && touched.contactName && (
                      <span id="contactName-error" className="field-error" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {errors.contactName}
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 2: Email + Phone */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      className={inputClass('email')}
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="your.email@company.com"
                      aria-invalid={!!errors.email}
                      aria-describedby="email-error"
                    />
                    {errors.email && touched.email && (
                      <span id="email-error" className="field-error" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {errors.email}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone Number *</label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      className={inputClass('phone')}
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="+1 (555) 000-0000"
                      aria-invalid={!!errors.phone}
                      aria-describedby="phone-error"
                    />
                    {errors.phone && touched.phone && (
                      <span id="phone-error" className="field-error" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {errors.phone}
                      </span>
                    )}
                  </div>
                </div>

                {/* Row 3: Country + Inquiry Type */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="country" className="form-label">Country / Region *</label>
                    <input
                      id="country"
                      type="text"
                      name="country"
                      className={inputClass('country')}
                      value={formData.country}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Your country"
                      aria-invalid={!!errors.country}
                      aria-describedby="country-error"
                    />
                    {errors.country && touched.country && (
                      <span id="country-error" className="field-error" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {errors.country}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="inquiryType" className="form-label">Inquiry Type *</label>
                    <Select
                      inputId="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleSelectChange}
                      onBlur={handleSelectBlur}
                      options={inquiryTypeOptions}
                      styles={customSelectStyles}
                      placeholder="Select inquiry type"
                      isSearchable={false}
                      aria-invalid={!!errors.inquiryType}
                      aria-describedby="inquiryType-error"
                    />
                    {errors.inquiryType && touched.inquiryType && (
                      <span id="inquiryType-error" className="field-error" role="alert">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                        {errors.inquiryType}
                      </span>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Message *
                    <span className="char-count">{formData.message.length} / 10 min</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className={`form-textarea${errors.message && touched.message ? ' input-error' : touched.message && !errors.message ? ' input-valid' : ''}`}
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows="6"
                    placeholder="Please provide details about your inquiry..."
                    aria-invalid={!!errors.message}
                    aria-describedby="message-error"
                  />
                  {errors.message && touched.message && (
                    <span id="message-error" className="field-error" role="alert">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                      {errors.message}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  className={`btn-submit-form hover-lift${apiLoading ? ' btn-loading' : ''}`}
                  disabled={apiLoading}
                >
                  {apiLoading ? (
                    <>
                      <span className="btn-spinner" aria-hidden="true" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Submit Inquiry
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Info Sidebar */}
            <div className="info-column scroll-reveal">
              <div className="info-card hover-lift">
                <h3 className="info-card-title">Direct Contact</h3>
                <div className="info-item">
                  <div className="info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div className="info-content">
                    <div className="info-label">Phone</div>
                    {/* <a href="tel:+919819829877" className="info-value">+91 9819829877</a> */}
                    <a href="tel:+971585829178" className="info-value">+971 585829178</a>
                    <a href="tel:+971585882917" className="info-value">+971 585882917</a>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div className="info-content">
                    <div className="info-label">Email</div>
                    <a href="mailto:info@effnetwork.com" className="info-value">
                      info@effnetwork.com
                    </a>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className="info-content">
                    <div className="info-label">Address</div>
                    <div className="info-value">Elite Freight Forwarders Network</div>
                    <div className="info-value-sub">Ajman Free Zone, Office – C1 – 1F – SF13979</div>
                    <div className="info-value-sub">P.O. Box: 932, Ajman, UAE</div>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div className="info-content">
                    <div className="info-label">Business Hours</div>
                    <div className="info-value">Monday – Friday</div>
                    <div className="info-value-sub">9:00 AM – 6:00 PM</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section scroll-reveal">
        <div className="container">
          <div className="trust-content">
            <h2 className="trust-title">Professional Communication You Can Rely On</h2>
            <p className="trust-description">
              All inquiries are managed with the highest level of confidentiality and professionalism.
              EFFN is committed to transparent, timely, and secure communication, ensuring every
              interaction reflects trust, discretion, and business integrity.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
